/**
 * @fileoverview Approval queue filter — pure state + predicate (MOR-906)
 *
 * Split out of `approvals-filter-bar.tsx` (react-refresh/only-export-components)
 * — `applyApprovalFilters` is the pure predicate `ApprovalsQueue` runs over
 * the loaded queue, so the filtering logic is unit-testable without mounting
 * the table (mirrors `src/calendar/components/header/task-filter-bar.helpers.ts`).
 */

import type { MultiSearchableSelectOption } from "@/ui/composed/inputs/multi-searchable-select";

/**
 * Client-side filter state. Empty arrays / blank strings ⇒ no constraint, so
 * `emptyFilters()` passes every entry through.
 */
export interface ApprovalFilterState {
  search: string;
  userIds: string[];
  jobIds: string[];
  categoryIds: string[];
  /** Inclusive ISO bounds (YYYY-MM-DD). Blank ⇒ unbounded on that side. */
  dateFrom: string;
  dateTo: string;
}

export function emptyFilters(): ApprovalFilterState {
  return {
    search: "",
    userIds: [],
    jobIds: [],
    categoryIds: [],
    dateFrom: "",
    dateTo: "",
  };
}

export function isEmptyFilters(f: ApprovalFilterState): boolean {
  return (
    f.search.trim() === "" &&
    f.userIds.length === 0 &&
    f.jobIds.length === 0 &&
    f.categoryIds.length === 0 &&
    f.dateFrom === "" &&
    f.dateTo === ""
  );
}

/** The queue fields the filters read — structural so tests need no Convex ids. */
export interface ApprovalFilterEntry {
  userId: string;
  userName: string;
  /** Absent for firm-overhead time (no matter). */
  jobId?: string;
  jobName: string;
  jobCode?: string;
  categoryId: string;
  categoryName: string;
  narrative: string;
  date: string;
  billable: boolean;
  projectCode?: "pre" | "post";
}

/**
 * Apply the filter state to the loaded queue.
 */
export function applyApprovalFilters<T extends ApprovalFilterEntry>(
  entries: T[],
  f: ApprovalFilterState,
): T[] {
  const userSet = new Set(f.userIds);
  const jobSet = new Set(f.jobIds);
  const categorySet = new Set(f.categoryIds);
  const search = f.search.trim().toLowerCase();

  return entries.filter((e) => {
    if (userSet.size > 0 && !userSet.has(e.userId)) return false;
    if (jobSet.size > 0 && (!e.jobId || !jobSet.has(e.jobId))) return false;
    if (categorySet.size > 0 && !categorySet.has(e.categoryId)) return false;

    // ISO dates compare lexicographically, so no parsing is needed.
    if (f.dateFrom !== "" && e.date < f.dateFrom) return false;
    if (f.dateTo !== "" && e.date > f.dateTo) return false;

    if (search !== "") {
      const haystack = [
        e.narrative,
        e.userName,
        e.jobName,
        e.jobCode ?? "",
        e.categoryName,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    return true;
  });
}

/**
 * Distinct option lists for the person / matter / category selects, derived
 * from the loaded queue so the bar only ever offers values that can match.
 */
export function deriveFilterOptions(entries: ApprovalFilterEntry[]): {
  people: MultiSearchableSelectOption[];
  matters: MultiSearchableSelectOption[];
  categories: MultiSearchableSelectOption[];
} {
  const people = new Map<string, string>();
  const matters = new Map<string, string>();
  const categories = new Map<string, string>();
  for (const e of entries) {
    people.set(e.userId, e.userName);
    if (e.jobId) {
      matters.set(
        e.jobId,
        e.jobCode ? `${e.jobCode} — ${e.jobName}` : e.jobName,
      );
    }
    categories.set(e.categoryId, e.categoryName);
  }
  const toOptions = (m: Map<string, string>) =>
    [...m.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  return {
    people: toOptions(people),
    matters: toOptions(matters),
    categories: toOptions(categories),
  };
}
